import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { doc, getDoc, updateDoc, Timestamp, deleteField } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { QuestionImageUpload, QuestionImageMetadata, UploadProgress } from '../types';

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const STORAGE_BASE_PATH = 'question-images';

/**
 * Get the storage path for a question image (grade-specific)
 * @param examCollection - The exam collection name
 * @param grade - The grade (9, 10, or 11)
 * @param questionNumber - The question number (e.g., "Q26")
 * @param variant - The variant (e.g., "A" or "B")
 * @returns The storage path
 */
const getStoragePath = (
  examCollection: string,
  grade: number,
  questionNumber: string,
  variant: string
): string => {
  return `${STORAGE_BASE_PATH}/${examCollection}/${grade}/questions/${questionNumber}_${variant}`;
};

/**
 * Get the Firestore metadata key for a question image (grade-specific)
 * @param grade - The grade (9, 10, or 11)
 * @param questionNumber - The question number
 * @param variant - The variant
 * @returns The metadata key for Firestore
 */
const getMetadataKey = (grade: number, questionNumber: string, variant: string): string => {
  return `${questionNumber}_${variant}`;
};

/**
 * Validate a file before upload
 * @param file - The file to validate
 * @throws Error if validation fails
 */
const validateFile = (file: File): void => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Yalnız JPG və PNG formatları qəbul olunur');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Fayl ölçüsü çox böyükdür. Maksimum 5MB.');
  }
};

/**
 * Upload a question image to Firebase Storage (grade-specific)
 * @param upload - The upload details
 * @returns The download URL
 */
export const uploadQuestionImage = async (
  upload: QuestionImageUpload
): Promise<string> => {
  try {
    // Validate file
    validateFile(upload.file);

    // Get file extension
    const fileExt = upload.file.name.split('.').pop() || 'jpg';
    const storagePath = getStoragePath(upload.examCollection, upload.grade, upload.questionNumber, upload.variant);
    const storageRef = ref(storage, `${storagePath}.${fileExt}`);

    // Upload file
    await uploadBytes(storageRef, upload.file);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    // Update Firestore metadata (include grade-specific structure)
    await updateQuestionImageMetadata(
      upload.examId,
      upload.grade,
      upload.questionNumber,
      upload.variant,
      downloadURL,
      upload.uploadedBy
    );

    return downloadURL;
  } catch (error: any) {
    console.error('Error uploading question image:', error);
    throw error;
  }
};

/**
 * Update question image metadata in Firestore (grade-specific structure)
 * @param examId - The exam document ID
 * @param grade - The grade (9, 10, or 11)
 * @param questionNumber - The question number
 * @param variant - The variant (e.g., "A" or "B")
 * @param url - The image URL
 * @param uploadedBy - The admin user ID
 */
const updateQuestionImageMetadata = async (
  examId: string,
  grade: number,
  questionNumber: string,
  variant: string,
  url: string,
  uploadedBy: string
): Promise<void> => {
  try {
    const examRef = doc(db, 'exams', examId);
    const examDoc = await getDoc(examRef);

    if (!examDoc.exists()) {
      throw new Error('İmtahan tapılmadı');
    }

    const existingData = examDoc.data();
    const existingExtraData = existingData.extraData || {};
    const existingQuestionImages = existingExtraData.questionImages || {};

    // Create grade-specific nested structure
    // Structure: { "9": { "Q26_A": {...} }, "10": { "Q26_A": {...} } }
    const gradeKey = grade.toString();
    const metadataKey = getMetadataKey(grade, questionNumber, variant);

    // Build nested structure
    const updatedQuestionImages = {
      ...existingQuestionImages,
      [gradeKey]: {
        ...(existingQuestionImages[gradeKey] || {}),
        [metadataKey]: {
          url,
          uploadedAt: Timestamp.now(),
          uploadedBy,
        },
      },
    };

    // Update extraData.questionImages with nested structure
    await updateDoc(examRef, {
      'extraData.questionImages': updatedQuestionImages,
    });
  } catch (error: any) {
    console.error('Error updating question image metadata:', error);
    throw error;
  }
};

/**
 * Get question image URL from Firestore (grade-specific with backward compatibility)
 * @param examId - The exam document ID
 * @param questionNumber - The question number
 * @param variant - The variant (e.g., "A" or "B")
 * @param grade - The grade (9, 10, or 11)
 * @returns The question image metadata or null
 */
export const getQuestionImageUrl = async (
  examId: string,
  questionNumber: string,
  variant: string,
  grade: number
): Promise<QuestionImageMetadata | null> => {
  try {
    const examRef = doc(db, 'exams', examId);
    const examDoc = await getDoc(examRef);

    if (!examDoc.exists()) {
      return null;
    }

    const examData = examDoc.data();
    const questionImages = examData?.extraData?.questionImages || {};

    const metadataKey = getMetadataKey(grade, questionNumber, variant);
    const gradeKey = grade.toString();

    // First try: New grade-specific nested structure
    if (questionImages[gradeKey] && questionImages[gradeKey][metadataKey]) {
      return questionImages[gradeKey][metadataKey];
    }

    // Grade 11 new blank = grade 10 layout (Q26, Q42…, Q75…).
    // If 11-ci sinif şəkli yüklənməyibsə, eyni nömrəni 10-cu sinifdən götür.
    if (grade === 11 && questionImages['10'] && questionImages['10'][metadataKey]) {
      return questionImages['10'][metadataKey];
    }

    // Second try: Old flat structure with variant (backward compatibility)
    if (questionImages[metadataKey]) {
      console.warn(`Question image for ${metadataKey} is using old format. Consider migrating to grade-specific structure.`);
      return questionImages[metadataKey];
    }

    // Third try: Very old format without variant (backward compatibility)
    if (questionImages[questionNumber]) {
      console.warn(`Question image for ${questionNumber} does not have variant suffix. Treating as variant ${variant} (backward compatibility).`);
      return questionImages[questionNumber];
    }

    return null;
  } catch (error: any) {
    console.error('Error getting question image URL:', error);
    return null;
  }
};

/**
 * Get all question images for an exam (organized by grade)
 * @param examId - The exam document ID
 * @returns Map of grade to question numbers to metadata
 */
export const getAllQuestionImages = async (
  examId: string
): Promise<Map<string, QuestionImageMetadata>> => {
  try {
    const examRef = doc(db, 'exams', examId);
    const examDoc = await getDoc(examRef);

    if (!examDoc.exists()) {
      return new Map();
    }

    const examData = examDoc.data();
    const questionImages = examData?.extraData?.questionImages || {};

    // Flatten nested structure for backward compatibility
    // New structure: { "9": { "Q26_A": {...} } }
    // Old structure: { "Q26_A": {...} }
    const flattenedMap = new Map<string, QuestionImageMetadata>();

    // Process new nested structure
    Object.entries(questionImages).forEach(([gradeOrKey, value]) => {
      // Check if this is a grade key (numeric string)
      if (['8', '9', '10', '11'].includes(gradeOrKey)) {
        // This is grade-specific data
        const gradeData = value as { [key: string]: QuestionImageMetadata };
        Object.entries(gradeData).forEach(([questionKey, metadata]) => {
          // Store with grade prefix for uniqueness: "9_Q26_A"
          flattenedMap.set(`${gradeOrKey}_${questionKey}`, metadata);
        });
      } else {
        // This is old flat structure
        // Store as-is for backward compatibility
        flattenedMap.set(gradeOrKey, value as QuestionImageMetadata);
      }
    });

    return flattenedMap;
  } catch (error: any) {
    console.error('Error getting all question images:', error);
    return new Map();
  }
};

/**
 * Delete a question image (grade-specific)
 * @param examId - The exam document ID
 * @param examCollection - The exam collection name
 * @param grade - The grade (9, 10, or 11)
 * @param questionNumber - The question number
 * @param variant - The variant (e.g., "A" or "B")
 */
export const deleteQuestionImage = async (
  examId: string,
  examCollection: string,
  grade: number,
  questionNumber: string,
  variant: string
): Promise<void> => {
  try {
    // Get current image metadata to find the URL
    const metadata = await getQuestionImageUrl(examId, questionNumber, variant, grade);

    if (!metadata) {
      throw new Error('Şəkil tapılmadı');
    }

    // Delete from Storage (try both extensions)
    const storagePathJpg = getStoragePath(examCollection, grade, questionNumber, variant) + '.jpg';
    const storagePathPng = getStoragePath(examCollection, grade, questionNumber, variant) + '.png';
    const storageRefJpg = ref(storage, storagePathJpg);
    const storageRefPng = ref(storage, storagePathPng);

    try {
      await deleteObject(storageRefJpg);
    } catch {
      // Try PNG
      try {
        await deleteObject(storageRefPng);
      } catch {
        // Ignore if file doesn't exist
      }
    }

    // Update Firestore (remove the question image metadata)
    const examRef = doc(db, 'exams', examId);
    const examDoc = await getDoc(examRef);

    if (examDoc.exists()) {
      const examData = examDoc.data();
      const questionImages = { ...(examData?.extraData?.questionImages || {}) };

      const gradeKey = grade.toString();
      const metadataKey = getMetadataKey(grade, questionNumber, variant);

      // Try to delete from new nested structure
      if (questionImages[gradeKey]) {
        const gradeData = { ...questionImages[gradeKey] };
        delete gradeData[metadataKey];

        // If grade has no more images, remove the grade key
        if (Object.keys(gradeData).length === 0) {
          delete questionImages[gradeKey];
        } else {
          questionImages[gradeKey] = gradeData;
        }
      } else {
        // Try to delete from old flat structure (backward compatibility)
        delete questionImages[metadataKey];
      }

      await updateDoc(examRef, {
        'extraData.questionImages': questionImages,
      });
    }
  } catch (error: any) {
    console.error('Error deleting question image:', error);
    throw error;
  }
};

/**
 * Update (replace) a question image (grade-specific)
 * @param upload - The upload details
 * @returns The new download URL
 */
export const updateQuestionImage = async (
  upload: QuestionImageUpload
): Promise<string> => {
  try {
    // Delete old image first (with grade-specific path)
    await deleteQuestionImage(upload.examId, upload.examCollection, upload.grade, upload.questionNumber, upload.variant);

    // Upload new image
    return await uploadQuestionImage(upload);
  } catch (error: any) {
    console.error('Error updating question image:', error);
    throw error;
  }
};

/**
 * Batch upload question images with atomic transaction
 * Either all images succeed or all are rolled back on failure
 * @param uploads - Array of upload details
 * @param onProgress - Callback function to report progress per image
 * @returns Map of upload key to download URL
 */
export const batchUploadQuestionImages = async (
  uploads: QuestionImageUpload[],
  onProgress?: (uploadKey: string, progress: UploadProgress) => void
): Promise<Map<string, string>> => {
  const successfulUploads: Map<string, { url: string; storageRef: any }> = new Map();
  const failedUploads: string[] = [];
  const results = new Map<string, string>();

  try {
    // Upload all images to Storage first
    for (const upload of uploads) {
      const uploadKey = `${upload.grade}_${upload.questionNumber}_${upload.variant}`;

      try {
        onProgress?.(uploadKey, { status: 'uploading', progress: 50 });

        // Validate file
        validateFile(upload.file);

        // Upload to Storage
        const fileExt = upload.file.name.split('.').pop() || 'jpg';
        const storagePath = getStoragePath(upload.examCollection, upload.grade, upload.questionNumber, upload.variant);
        const storageRef = ref(storage, `${storagePath}.${fileExt}`);
        await uploadBytes(storageRef, upload.file);
        const downloadURL = await getDownloadURL(storageRef);

        // Track successful upload for potential rollback
        successfulUploads.set(uploadKey, { url: downloadURL, storageRef });

        onProgress?.(uploadKey, { status: 'uploading', progress: 100 });

      } catch (error: any) {
        console.error(`Failed to upload ${uploadKey}:`, error);
        failedUploads.push(uploadKey);
        onProgress?.(uploadKey, { status: 'error', error: error.message });
        throw error; // Throw to trigger rollback
      }
    }

    // If all Storage uploads succeeded, update Firestore metadata
    for (const upload of uploads) {
      const uploadKey = `${upload.grade}_${upload.questionNumber}_${upload.variant}`;

      try {
        await updateQuestionImageMetadata(
          upload.examId,
          upload.grade,
          upload.questionNumber,
          upload.variant,
          successfulUploads.get(uploadKey)!.url,
          upload.uploadedBy
        );

        results.set(uploadKey, successfulUploads.get(uploadKey)!.url);
        onProgress?.(uploadKey, { status: 'success', progress: 100 });

      } catch (error: any) {
        console.error(`Failed to update metadata for ${uploadKey}:`, error);
        onProgress?.(uploadKey, { status: 'error', error: error.message });
        throw error; // Throw to trigger rollback
      }
    }

    return results;

  } catch (error: any) {
    // Rollback: Delete all successfully uploaded images from Storage
    console.error('Batch upload failed, rolling back...', error);

    const rollbackPromises = Array.from(successfulUploads.entries()).map(
      async ([uploadKey, data]) => {
        try {
          await deleteObject(data.storageRef);
          console.log(`Rolled back ${uploadKey}`);
        } catch (rollbackError) {
          console.error(`Failed to rollback ${uploadKey}:`, rollbackError);
        }
      }
    );

    await Promise.allSettled(rollbackPromises);

    throw new Error(`Batch upload failed: ${error.message}. All changes have been rolled back.`);
  }
};
